// Ported from dev-scrips-pm-react src/billing/components/claim-review/clinical-note-panel.tsx (PROD-1119 cross-module closure).
import { useRef } from 'react';
// GAP: no DS icon for the chief complaint section (MessageSquareText); lucide in both modes (design sheet §3.1, logged not substituted).
import {
  ClipboardList,
  FolderClosed,
  MessageSquareText,
  PencilLine,
  Search,
  Stethoscope,
} from 'lucide-react';
// Drawer migration (2026-09-20): replaces the local shadcn Sheet. Drawer is
// props-based (open/onClose/title/headerActions/...), not composed
// subcomponents — see the call site below for the restructure.
import { Drawer, Modal, Skeleton, Avatar, printNode } from '@scripsteam/scrips-signal-ds';
import {
  useEncounterClinicalDetails,
  useEncounterVitals,
  usePatientClinicalSummary,
} from '@/billing/services/use-clinical-note';
import {
  ageYearsFromText,
  buildVitalRows,
} from '@/billing/lib/clinical-note-vitals';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.
import { v2Look, V2_EYEBROW } from '@/shared/ui/design-v2';

/**
 * View Clinical Note — read-only side panel (PROD-969c phase 3). Mirrors the
 * Flutter `ClinicalNotes` / generated-notes view
 * (`vital_component_widget`, `ui_data.emptyVitalUiList`,
 * `review_claim_popup.dart:3237`):
 *
 *   Header (Clinical note · Done) → patient block → REASON FOR VISIT → VITALS
 *   (fixed list) → PATIENT SUMMARY (allergies / problems / surgical /
 *   vaccinations / family) → Chief Complaint · Risk Assessment · Review of
 *   Systems · Physical Exam · Docs · Assessment & Plan (diagnoses + meds).
 *
 * READ ONLY. Composes `/api/Patients` reads (option B — surfaced in billing).
 *
 * VISIBILITY / GATE (don't "align" these incorrectly): the View Clinical Note
 * button is ALWAYS visible — same as Flutter (`review_claim_popup.dart:3218`,
 * no per-button permission). Access is governed by the Review Claim popup's
 * own claim-access gate (you can't open this panel without opening the popup),
 * NOT by a per-button permission. Do NOT add a standalone permission gate on
 * the button to "match a backend role" — there isn't one in Flutter.
 *
 * DS note: Flutter colours the section glyphs (green/orange/purple/blue). The
 * Scrips DS bans decorative green/yellow (clinical-signal-only), so all section
 * glyph tiles use brand-primary here — structure + content match the spec.
 */
/**
 * Minimal patient header context the panel renders (name / MRN / DOB / age /
 * gender). `InvoiceDetailPatient` (claims) and the chart `Patient` (Notes tab,
 * after mapping `genderName`) both satisfy this shape.
 */
export interface ClinicalNotePatientContext {
  name?: string | null;
  mrn?: string | null;
  birthDate?: string | null;
  age?: string | null;
  genderName?: string | null;
}

export interface ClinicalNoteProviderContext {
  name?: string | null;
  imageUrl?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Encounter + patient ids drive the three clinical-detail fetches. */
  encounterId?: string | null;
  patientId?: string | null;
  /** Demographics for the header block. */
  patient?: ClinicalNotePatientContext;
  /** Provider who held the encounter (header "Created by"). */
  provider?: ClinicalNoteProviderContext;
  /** ISO encounter date/time (header line). */
  encounterDate?: string | null;
  /**
   * Show a Print button in the header. Notes-tab view only (Flutter
   * `notes_tab.dart` Print) — the claims view keeps just Done.
   */
  showPrint?: boolean;
  /**
   * Optional extra header action rendered left of Done — e.g. the Notes
   * tab's "View PVS" affordance. Claims passes nothing.
   */
  headerExtra?: React.ReactNode;
  /**
   * A centred DS Modal instead of the right-side drawer (Antonin, 4 Nov: "Change this to a central modal"). Same
   * header, content and width (640, the Modal's `lg` step). The encounter and the chart's Notes tab pass it under
   * `layoutFixes`; the claims view keeps its drawer.
   */
  centred?: boolean;
}

function fmtDate(s?: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function fmtTime(s?: string | null): string {
  if (!s) return '';
  try {
    const d = new Date(s);
    const h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, '0');
    const hour12 = h % 12 || 12;
    return `${hour12}:${m}${h >= 12 ? 'p' : 'a'}`;
  } catch {
    return '';
  }
}

function joinDot(values: Array<string | undefined | null>): string {
  const v = values.filter((x): x is string => Boolean(x && x.trim()));
  return v.length ? v.join(' • ') : '—';
}

export function ClinicalNotePanel({
  open,
  onOpenChange,
  encounterId,
  patientId,
  patient,
  provider,
  encounterDate,
  showPrint = false,
  headerExtra,
  centred = false,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);

  // The detail hooks accept `string | undefined`; the panel's id props are
  // `string | null` (Notes passes `?? undefined`, claims passes the invoice
  // field which may be nullable) — coerce null → undefined at the boundary.
  const eId = encounterId ?? undefined;
  const pId = patientId ?? undefined;
  const detailsQ = useEncounterClinicalDetails({ encounterId: eId, patientId: pId, enabled: open });
  const summaryQ = usePatientClinicalSummary({ patientId: pId, enabled: open });
  const vitalsQ = useEncounterVitals({ encounterId: eId, patientId: pId, enabled: open });

  const details = detailsQ.data;
  const summary = summaryQ.data;
  const loading = detailsQ.isLoading || summaryQ.isLoading || vitalsQ.isLoading;

  const encounter = details?.enconterDetails?.[0];
  const reasonForVisit = encounter?.reasonForVisit ?? '—';
  const location = encounter?.locationName;

  const vitalRows = buildVitalRows({
    vitals: vitalsQ.data ?? [],
    ageYears: ageYearsFromText(patient?.age),
    gender: patient?.genderName,
  });

  const complaints = (details?.chiefComplaints?.complaints ?? []).filter(Boolean);
  const conditions = (details?.assessmentResponse?.[0]?.assessmentPlanCondition ?? [])
    .filter((c) => !c.isDeleted);
  const meds = conditions.flatMap((c) =>
    (c.childMedicalCode?.assessmentPlanMedication ?? [])
      .filter((m) => !m.isDeleted)
      .map((m) => ({ dxCode: c.conditionCode?.code, med: m })),
  );
  const rosEmpty = (details?.rosDetails?.length ?? 0) === 0;
  const peEmpty = (details?.pEDetails?.length ?? 0) === 0;

  const title = (
    <span data-cn="title" className="text-base font-bold text-text-primary">
      Clinical note
    </span>
  );
  const actions = (
    <div className="flex items-center gap-4">
      {headerExtra}
      {showPrint ? (
        <button
          type="button"
          onClick={() =>
            printNode(contentRef.current, { title: 'Clinical note' })
          }
          data-cn="action"
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          Print
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        data-cn="action"
        className="text-sm font-medium text-brand-primary hover:underline"
      >
        Done
      </button>
    </div>
  );
  const body = (
    <div
      ref={contentRef}
      data-cn="root"
      className="flex-1 min-h-0 overflow-auto scrollbar-hide px-6 py-5 flex flex-col gap-6"
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-full" />
        </div>
      ) : (
        <>
          {/* Patient header block */}
          <div>
            <p className="text-sm font-semibold text-text-primary">
              {joinDot([
                patient?.name,
                patient?.mrn ? `MRN ${patient.mrn.replace(/^MRN\s*/i, '')}` : undefined,
                patient?.birthDate ? `DOB: ${fmtDate(patient.birthDate)}` : undefined,
              ])}
            </p>
            <p className="text-sm text-text-secondary mt-1">
              {joinDot([
                location,
                fmtDate(encounterDate),
                fmtTime(encounterDate),
              ])}
            </p>
            {provider?.name ? (
              <div className="flex items-center gap-2 mt-2 text-sm text-text-secondary">
                <span>Created by</span>
                <Avatar
                  scale="compact"
                  name={provider.name}
                  src={provider.imageUrl ?? undefined}
                  tone="provider"
                  size="sm"
                />
                <span className="text-text-primary">{provider.name}</span>
              </div>
            ) : null}
          </div>

          <Labeled label="Reason for visit">
            <p className="text-lg font-semibold text-text-primary lowercase first-letter:uppercase">
              {reasonForVisit}
            </p>
          </Labeled>

          <Labeled label="Vitals">
            <div className="flex flex-col">
              {vitalRows.map((v) => (
                <div
                  key={v.label}
                  className="flex items-center justify-between py-1.5 border-b border-border-subtle last:border-0"
                >
                  <span className="text-sm text-text-secondary">{v.label}</span>
                  <span
                    className={
                      v.abnormal
                        ? 'text-sm tabular-nums text-status-amber-text'
                        : 'text-sm tabular-nums text-text-primary'
                    }
                  >
                    {v.value}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-sm text-brand-primary mt-3">
              Vitals need to be reviewed
            </p>
          </Labeled>

          <Labeled label="Patient Summary">
            <dl className="flex flex-col gap-3">
              <SummaryRow
                term="Allergies"
                value={joinDot((summary?.allergies ?? []).filter((a) => !a.isDeleted).map((a) => a.code?.display))}
              />
              <SummaryRow
                term="Active Problems"
                value={joinDot((summary?.activeConditions ?? []).filter((c) => !c.isDeleted).map((c) => c.code?.display))}
              />
              <SummaryRow
                term="Past Surgical History"
                value={joinDot((summary?.procedures ?? []).filter((p) => !p.isDeleted).map((p) => p.code?.displayName ?? p.code?.display))}
              />
              <SummaryRow
                term="Vaccinations History"
                value={joinDot((summary?.immunization ?? []).filter((i) => !i.isDeleted).map((i) => i.vaccine?.display))}
              />
              <SummaryRow
                term="Family History"
                value={joinDot((summary?.familyHistory ?? []).filter((f) => !f.isDeleted).flatMap((f) => (f.code ?? []).map((c) => c.display)))}
              />
            </dl>
          </Labeled>

          <IconSection icon={<MessageSquareText className="size-4" />} title="Chief Complaint">
            {complaints.length === 0 ? (
              <Empty label="No chief complaint yet." />
            ) : (
              <div className="flex flex-col gap-2">
                {complaints.map((c, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <span className="text-text-muted shrink-0">Chief Complaint</span>
                    <span className="text-text-primary">
                      {c.coding?.display ?? '—'}
                      {c.complaintCategory ? ` (${c.complaintCategory})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </IconSection>

          <IconSection icon={<PencilLine className="size-4" />} title="Risk Assessment">
            <Empty label="No Risk Assessment yet." />
          </IconSection>

          <IconSection icon={<Search className="size-4" />} title="Review of Systems">
            {rosEmpty ? <Empty label="No ROS yet." /> : <Empty label="See encounter." />}
          </IconSection>

          <IconSection icon={<Stethoscope className="size-4" />} title="Physical Exam">
            {peEmpty ? <Empty label="No Physical Exam yet." /> : <Empty label="See encounter." />}
          </IconSection>

          <IconSection icon={<FolderClosed className="size-4" />} title="Docs">
            <Empty label="No Docs yet." />
          </IconSection>

          <IconSection icon={<ClipboardList className="size-4" />} title="Assessment & Plan">
            <p className={v2Look('text-xs font-bold uppercase tracking-wide text-brand-depth mb-2', V2_EYEBROW)}>
              Diagnosis
            </p>
            {conditions.length === 0 ? (
              <Empty />
            ) : (
              <div className="flex flex-col gap-2">
                {conditions.map((c, i) => (
                  <div key={c.identifier ?? i} className="flex gap-3 text-sm">
                    <span className="text-text-muted shrink-0 w-14">
                      {c.conditionCode?.code ?? '—'}
                    </span>
                    <span className="text-text-primary">
                      {c.conditionCode?.display ?? '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className={v2Look('text-xs font-bold uppercase tracking-wide text-brand-depth mt-4 mb-2', V2_EYEBROW)}>
              Medications
            </p>
            {meds.length === 0 ? (
              <Empty />
            ) : (
              <div className="flex flex-col gap-2">
                {meds.map(({ dxCode, med }, i) => (
                  <div key={med.identifier ?? i} className="flex gap-3 text-sm">
                    <span className="text-text-muted shrink-0 w-14">{dxCode ?? ''}</span>
                    <div className="min-w-0">
                      <span className="text-brand-primary">
                        {med.medicationCode?.display ?? med.medicationCode?.code ?? '—'}
                      </span>
                      {med.notes ? (
                        <div className="text-sm text-text-muted mt-1">
                          {med.notes}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </IconSection>
        </>
      )}
    </div>
  );

  if (centred) {
    return (
      <Modal
        open={open}
        onClose={() => onOpenChange(false)}
        title="Clinical note"
        titleHidden
        size="lg"
        maxHeight="app-viewport"
        variant="bleed"
        scrollBody
        bodyLayout="column"
        className="bg-surface-card"
      >
        {/* The drawer's header row (title, Print, Done) as the dialog's own row; only the note below scrolls. */}
        <div data-cn="header" className="flex shrink-0 items-center justify-between gap-4 border-b border-border-default px-6 py-4">
          {title}
          {actions}
        </div>
        {body}
      </Modal>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      side="right"
      // Width was `data-[side=right]:w-[640px] data-[side=right]:!max-w-none`
      // on the retired Sheet primitive; Drawer takes width as a plain prop
      // (an inline style, not a class), so that idiom no longer applies —
      // the numeric value (640) is unchanged.
      width={640}
      showCloseButton={false}
      className="bg-surface-card"
      // Trailing Print + Done pair beside the title — straight
      // `headerActions` (no leading slot needed here, unlike
      // new-file-popup.tsx's Cancel/Save pair).
      // NOTE: the retired Sheet header used `relative + justify-center +
      // absolute right-6` to keep "Clinical note" TRULY centred regardless
      // of the actions' width. Drawer's title/headerActions row is
      // `justify-between` with no leading slot to balance headerActions —
      // so the title now sits left, not centred, unless a future
      // `headerLeading` spacer is added. Flagging rather than hacking a fix
      // in (see new-file-popup.tsx's identical caveat, and the Drawer
      // contract's own "NOT a centring mechanism" note).
      title={title}
      ariaLabel="Clinical note"
      headerActions={actions}
    >
      {body}
    </Drawer>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-bold uppercase tracking-wide text-text-muted mb-2">
        {label}
      </h3>
      {children}
    </section>
  );
}

function SummaryRow({ term, value }: { term: string; value: string }) {
  return (
    <div className="grid grid-cols-label-value gap-3">
      <dt className="text-sm text-text-muted">{term}</dt>
      <dd className="text-sm text-text-primary">{value}</dd>
    </div>
  );
}

function IconSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-border-default pt-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-brand-primary text-white">
          {icon}
        </span>
        <h3 className="text-base font-bold text-brand-depth">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Empty({ label = 'None recorded' }: { label?: string }) {
  return <p className="text-sm text-text-muted">{label}</p>;
}
